import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { usePursuit } from '../contexts/PursuitContext';
import PolicyAcceptanceModal from '../components/modals/PolicyAcceptanceModal';
import {
  ArrowLeft,
  Shield,
  Loader2,
  AlertCircle,
  Building2,
  Briefcase,
  ArrowRight,
  UserPlus,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const { loginWithJWT } = useAuth();
  const { clearPursuitState } = usePursuit();

  // Email step
  const [email, setEmail] = useState('');
  const [isCheckingRole, setIsCheckingRole] = useState(false);

  // SA detection — null = unchecked, true/false = result
  const [isSuperAdmin, setIsSuperAdmin] = useState(null);
  // 'otp' | 'password' — shown only to SA after they choose
  const [loginMode, setLoginMode] = useState(null);

  // Password login state (SA only)
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGetStartedModal, setShowGetStartedModal] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const handleInviteSubmit = () => {
    if (inviteToken.trim()) {
      navigate(`/invite/${inviteToken.trim()}`);
    }
  };

  const handlePolicyAccepted = () => {
    setShowPolicyModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  // Reset to email step (e.g. user wants to change email)
  const handleBackToEmail = () => {
    setIsSuperAdmin(null);
    setLoginMode(null);
    setPassword('');
    setError(null);
  };

  // Step 1: email submitted — check if SA, then branch
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsCheckingRole(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/check-super-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      const sa = data.is_super_admin === true;
      setIsSuperAdmin(sa);
      if (!sa) {
        // Not SA — skip the choice and go straight to OTP
        await sendOtp();
      }
      // SA sees the choice panel — no further action needed here
    } catch {
      // On error just fall back to OTP to not break non-SA login
      setIsSuperAdmin(false);
      await sendOtp();
    } finally {
      setIsCheckingRole(false);
    }
  };

  // Send OTP and navigate to verify page
  const sendOtp = useCallback(async () => {
    setIsLoading(true);
    clearPursuitState();
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to send login code');
      navigate(`/verify-otp?email=${encodeURIComponent(email.trim())}`);
    } catch (err) {
      setError(err.message || 'Failed to send login code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [email, navigate, clearPursuitState]);

  // SA chose OTP path
  const handleChooseOtp = async () => {
    setLoginMode('otp');
    await sendOtp();
  };

  // SA chose password path — show password field
  const handleChoosePassword = () => {
    setLoginMode('password');
    setError(null);
  };

  // SA password login submit
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    if (!password) { setError('Please enter your password'); return; }
    setIsLoading(true);
    clearPursuitState();
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Invalid credentials');
      if (data.access_token && data.user) {
        localStorage.removeItem('auth_token');
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        loginWithJWT(data.access_token, data.user);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  // Determine which form to render
  const showEmailForm = isSuperAdmin === null;
  const showSAChoice = isSuperAdmin === true && loginMode === null;
  const showPasswordForm = isSuperAdmin === true && loginMode === 'password';

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.2) 0%, transparent 50%)' }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">F</span>
              </div>
              <span className="text-white font-semibold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                FedBD
              </span>
            </div>
          </div>
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Federal BD
              <span className="block text-teal-400">Enablement Platform</span>
            </h2>
            <p className="text-slate-400 text-base max-w-md leading-relaxed">
              Find opportunities with SAM.gov Sentinel. Analyze RFPs with AI.
              Make smarter Go/No-Go decisions and win more contracts.
            </p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Shield className="w-4 h-4" />
              <span>SOC 2 Compliant • FedRAMP Ready</span>
            </div>
            <div className="border-t border-slate-700 pt-3">
              <p className="text-xs text-slate-500">
                Powered by{' '}
                <span className="text-teal-400 font-semibold">MNGC Group, Inc.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8 transition-colors"
            data-testid="back-to-landing-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to home</span>
          </button>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-teal-700 rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">F</span>
            </div>
            <span className="font-semibold text-slate-900 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              FedBD
            </span>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Sign in
              </CardTitle>
              <CardDescription className="text-slate-500">
                {showSAChoice
                  ? 'Choose how you want to authenticate'
                  : showPasswordForm
                  ? 'Enter your Super Admin password'
                  : 'Enter your email to receive a secure login code'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* ── Step 1: Email form ── */}
              {showEmailForm && (
                <form onSubmit={handleEmailSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(null); }}
                      className="border-slate-200 focus:border-teal-600 focus:ring-teal-600/20"
                      data-testid="login-email-input"
                      required
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    We'll send a 6-digit code to your email to sign you in securely.
                  </p>
                  <Button
                    type="submit"
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white btn-lift"
                    disabled={isCheckingRole || isLoading || !email.trim()}
                    data-testid="login-submit-btn"
                  >
                    {(isCheckingRole || isLoading) ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Continuing...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" />Continue</>
                    )}
                  </Button>
                </form>
              )}

              {/* ── Step 2 (SA only): Choose login method ── */}
              {showSAChoice && (
                <div className="space-y-4">
                  {/* SA badge */}
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                    <span>Super Admin account detected for <strong>{email}</strong></span>
                  </div>

                  <p className="text-sm text-slate-600">Choose your authentication method:</p>

                  <Button
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                    onClick={handleChooseOtp}
                    disabled={isLoading}
                    data-testid="sa-otp-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending code...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" />Send login code (OTP &amp; 2FA)</>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full border-slate-200"
                    onClick={handleChoosePassword}
                    data-testid="sa-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Sign in with password
                  </Button>

                  <button
                    onClick={handleBackToEmail}
                    className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors mt-1"
                  >
                    ← Use a different email
                  </button>
                </div>
              )}

              {/* ── Step 2b (SA password form) ── */}
              {showPasswordForm && (
                <form onSubmit={handlePasswordLogin} className="space-y-5">
                  <div className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                    <ShieldCheck className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                    <span>Signing in as <strong>{email}</strong></span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-slate-700">Password</Label>
                      {/* Only rendered on the Super Admin password path — the
                          OTP/TOTP flow has no password to reset. Email is passed
                          via router state rather than a query param so it never
                          lands in a URL, browser history, or referrer header. */}
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password', { state: { email: email.trim() } })}
                        className="text-xs text-teal-700 hover:text-teal-800 hover:underline transition-colors"
                        data-testid="sa-forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(null); }}
                        className="border-slate-200 focus:border-teal-600 pr-10"
                        data-testid="sa-password-input"
                        autoFocus
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white btn-lift"
                    disabled={isLoading || !password}
                    data-testid="sa-password-submit-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</>
                    ) : (
                      <><Lock className="w-4 h-4 mr-2" />Sign in</>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={() => { setLoginMode(null); setPassword(''); setError(null); }}
                    className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    ← Back to authentication options
                  </button>
                </form>
              )}

              {/* Sign up link — only on email step */}
              {showEmailForm && (
                <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                  <p className="text-sm text-slate-500">
                    Don't have an account?{' '}
                    <button
                      onClick={() => setShowGetStartedModal(true)}
                      className="text-teal-700 hover:text-teal-800 font-medium hover:underline"
                    >
                      Register your organization
                    </button>
                  </p>
                </div>
              )}

              {/* Role descriptions - only on email step */}
              {showEmailForm && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
                    3-Tier Role Hierarchy
                  </p>
                  <div className="space-y-3 text-xs text-slate-500">
                    <div className="p-2 bg-slate-50 rounded">
                      <span className="font-medium text-slate-700">Tier 1 - Super Admin:</span>
                      <span className="block mt-0.5">Platform owner. Manages all consultants and global settings.</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <span className="font-medium text-slate-700">Tier 2 - Consultant:</span>
                      <span className="block mt-0.5">Service provider managing multiple client portfolios.</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <span className="font-medium text-slate-700">Tier 3 - Contractor:</span>
                      <span className="block mt-0.5">End user (Admin, Bid Manager, or Reviewer role).</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center text-xs text-slate-400">
            © 2026 FedBD. Owned and Operated by{' '}
            <span className="text-teal-600 font-medium">MNGC Group, Inc.</span>
          </div>
        </div>
      </div>

      {/* Get Started Modal */}
      <Dialog open={showGetStartedModal} onOpenChange={setShowGetStartedModal}>
        <DialogContent className="sm:max-w-lg" data-testid="get-started-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Get Started with FedBD
            </DialogTitle>
            <DialogDescription>
              Choose how you'd like to join the platform
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Option 1: Register as Contractor */}
            <button
              onClick={() => { setShowGetStartedModal(false); navigate('/pricing?type=contractor'); }}
              className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left group"
              data-testid="register-contractor-option"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                <Building2 className="w-6 h-6 text-teal-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  I'm a Contractor
                </h3>
                <p className="text-sm text-slate-500">
                  Federal contractor looking to discover opportunities, analyze RFPs, and win more contracts with AI.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-teal-600 mt-1 transition-colors" />
            </button>

            {/* Option 2: Register as Consultant */}
            <button
              onClick={() => { setShowGetStartedModal(false); navigate('/pricing?type=consultant'); }}
              className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-all text-left group"
              data-testid="register-consultant-option"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                <Briefcase className="w-6 h-6 text-purple-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  I'm an Independent Consultant
                </h3>
                <p className="text-sm text-slate-500">
                  BD consultancy helping multiple contractors find and win federal contracts. Manage all clients from one dashboard.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 mt-1 transition-colors" />
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">or</span>
              </div>
            </div>

            {/* Option 3: Join via Invitation */}
            <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <UserPlus className="w-6 h-6 text-blue-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Join an Existing Organization
                  </h3>
                  <p className="text-sm text-slate-500">
                    Enter the invitation code from your email to join your team's workspace.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Enter invitation code (e.g., abc123)"
                    value={inviteToken}
                    onChange={(e) => setInviteToken(e.target.value)}
                    className="pl-10 border-slate-200"
                    data-testid="invite-code-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleInviteSubmit(); }}
                  />
                </div>
                <Button
                  onClick={handleInviteSubmit}
                  disabled={!inviteToken.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="join-with-invite-btn"
                >
                  Join
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Policy Acceptance Modal */}
      <PolicyAcceptanceModal
        isOpen={showPolicyModal}
        onAccepted={handlePolicyAccepted}
      />
    </div>
  );
}
